import { useEffect, useRef, useState } from 'preact/hooks';
import { startTransition } from 'preact/compat';
import { Subject } from 'helpers/store';

/**
 * Problem we saw:
 * - During lazy/Suspense transitions and when the legacy Angular bundle or other scripts also touch the DOM, we sometimes hit this browser error:
 *   "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node." (a NotFoundError)
 * - That error means the element Preact wanted to insert next to has just moved or been removed by something else. In other words, we had a race between our render and other DOM work.
 *
 * Why it happened:
 * - Preact uses markers and siblings to place new nodes. If a state update lands exactly while Suspense is swapping trees or external scripts are moving DOM nodes, the reference node may no longer be where Preact expects.
 * - Our global store emitted values immediately, and we set component state immediately. That pushed Preact to commit DOM changes right in the middle of those DOM mutations.
 *
 * What the fix changes:
 * 1) Coalesce updates
 *    - We keep only the latest store value, so a burst of emissions triggers a single render. Less churn means fewer chances to hit the race window.
 *
 * 2) Schedule commits in a safe window
 *    - First we hop to the next macrotask using `setTimeout(..., 0)`. That gets us out of the current synchronous work.
 *    - Then we use `requestAnimationFrame` (rAF). rAF runs our callback right before the next browser paint, after Suspense/lazy swaps and other DOM changes have settled. At that point, the DOM is stable.
 *
 * 3) Guard against unmounts
 *    - If the component unmounts (which can happen during transitions), we cancel any scheduled timers/frames and skip the update. No state updates to unmounted components.
 *
 * 4) Lower the priority when possible
 *    - When `startTransition` is available, we wrap the setState so it’s treated as low-priority, reducing synchronous DOM churn during interactive work.
 *
 * Special case for tests:
 * - In NODE_ENV=test we skip the timers and run synchronously to keep Jest deterministic.
 *
 * The net effect:
 * - Preact commits its DOM changes when the tree is no longer in flux. The reference node exists where expected, so `insertBefore` succeeds. The intermittent NotFoundError disappears without changing public APIs or app behavior.
 *
 * Glossary:
 * - rAF (requestAnimationFrame): a browser API that schedules a callback to run right before the next paint. It’s a good moment to apply UI updates that should happen after the DOM has settled.
 */
export const useConnect = <T>(storeValue: Subject<T>, initialState: T = null) => {
  const [value, setValue] = useState<T>(initialState ?? storeValue?.lastValue ?? null);
  const mountedRef = useRef(true);
  const lastDataRef = useRef<T>(value);
  const timeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const isTestEnv = typeof process !== 'undefined' && process?.env?.NODE_ENV === 'test';

  useEffect(() => {
    mountedRef.current = true;

    if (!storeValue) return () => {};

    const applyUpdate = () => {
      if (!mountedRef.current) return;
      const next = lastDataRef.current;
      const apply = () => setValue((prev) => (prev === next ? prev : next));

      const st: any = startTransition as any;
      if (typeof st === 'function') {
        st(apply);
      } else {
        apply();
      }
    };

    const scheduleCommit = () => {
      // Coalesce multiple rapid emissions into one render and schedule after paint
      if (pendingRef.current) return;
      pendingRef.current = true;

      // In tests, run synchronously to avoid timing-related flakes
      if (isTestEnv) {
        try {
          applyUpdate();
        } finally {
          pendingRef.current = false;
        }
        return;
      }

      // Use a macrotask + rAF (requestAnimationFrame) to avoid colliding with DOM mutations from Suspense
      timeoutRef.current = setTimeout(() => {
        const run = () => {
          try {
            applyUpdate();
          } finally {
            pendingRef.current = false;
            rafRef.current = null;
          }
        };

        // rAF = requestAnimationFrame: runs the callback right before the next browser paint,
        // letting the DOM settle and avoiding conflicts with Suspense/lazy swaps.
        if (typeof requestAnimationFrame === 'function') {
          rafRef.current = requestAnimationFrame(run) as any;
        } else {
          // Fallback in non-browser/JSDOM environments
          run();
        }

        timeoutRef.current = null;
      }, 0) as unknown as number;
    };

    const unsubscribe = storeValue.subscribe((data: T) => {
      lastDataRef.current = data as T;
      scheduleCommit();
    });

    return () => {
      mountedRef.current = false;
      pendingRef.current = false;

      if (rafRef.current != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(rafRef.current);
      }
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }

      rafRef.current = null;
      timeoutRef.current = null;

      unsubscribe?.();
    };
    // Re-subscribe if the subject instance changes
  }, [storeValue]);

  const updateValue = (data?: T) => {
    storeValue?.next(data);
  };

  return [value, updateValue] as [T, typeof updateValue];
};
